import client from '@onpace/onspace-core/components/client'
import translation from '@onpace/onspace-core/components/translations'

const CHANGE_EVENT_INPUTS = ['checkbox', 'color', 'file', 'radio', 'range']
const AUTOSAVE_PERIOD = 30_000

document.addEventListener('turbo:load', (_event) => {
  document.querySelectorAll('form').forEach((form) => form.onspaceSetup())
})

document.addEventListener('turbo:frame-load', (_event) => {
  document.querySelectorAll('form').forEach((form) => form.onspaceSetup())
})

document.addEventListener('turbo:click', (event) => {
  const action = event.target.getAttribute('data-turbo-action')
  if (typeof action === 'string' && (action !== 'advance' || action !== 'replace')) { return }

  const targetFrame = event.target.onspaceTurboTarget || document
  const autosaveForms = targetFrame.querySelectorAll('form[onspace-autosave-url]')
  autosaveForms.forEach((form) => form.onspaceAutosaveIfChanged())
})

window.addEventListener('beforeunload', (_event) => {
  const autosaveForms = document.querySelectorAll('form[onspace-autosave-url]')
  autosaveForms.forEach((form) => form.onspaceAutosaveIfChanged())
})

/// Sets up forms with Onspace functionality.
///
/// This configures change detection and autosave functionality.
HTMLFormElement.prototype.onspaceSetup = function() {
  this.addEventListener('submit', this.onspaceHandleSubmission.bind(this))

  this.onspacePromptsChanges = this.getBooleanAttribute('onspace-prompt-changes')
  this.onspaceSubmitsChanges = this.getBooleanAttribute('onspace-submit-changes')
  this.onspaceAutosaveUrl = this.getAttribute('onspace-autosave-url')

  this.onspaceAutosaveButtons = this.querySelectorAll('[onspace-autosave-button]')
  this.onspaceAutosaveIndicators = this.querySelectorAll('[onspace-autosave-updated]')

  this.onspaceAutosaveButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      const form = button.closest('form')
      form.onspaceAutosave(button)

      event.preventDefault()
      event.stopPropagation()
    })
  })

  this.onspaceListenForChanges()
  this.onspaceRestoreLocalSave()
}

////////// Changes

/// Responds to change events on the this.
///
/// When an event is received, it is first checked that the event is a valid change event, as there are some events that
/// may be triggered here that don't indicate something on the form is changed. It then marks the form as changed, and
/// triggers the +onspace:change+ event.
///
/// This is setup by HTMLFormElement.onspaceListenForChanges and should not be called directly.
HTMLFormElement.prototype.onspaceElementChanged = function(event) {
  const eventElement = event.target.closest('form-item, input, input-array, input-association, rich-text, select, textarea')
  if (!eventElement) { return }

  switch (eventElement.tagName) {
  case 'INPUT':
    if (event.type === 'change' && !CHANGE_EVENT_INPUTS.includes(eventElement.type)) { return }
    if (event.type !== 'change' && CHANGE_EVENT_INPUTS.includes(eventElement.type)) { return }
    break
  case 'TEXTAREA':
    if (event.type === 'change') { return }
  }

  if (this.onspaceSubmitsChanges) {
    this.requestSubmit()
  }

  this.onspaceHasChanges = true
  this.triggerEvent('onspace:form:change')

  this.onspaceQueueAutosave()
}

/// Listens for changes on all of the form's controls.
///
/// This adds listeners to itself, which will trigger when that event propagates from any of it's children. All event
/// listeners will use HTMLFormElement.onspaceElementChanged as their callback.
HTMLFormElement.prototype.onspaceListenForChanges = function() {
  this.addEventListener('input', this.onspaceElementChanged.bind(this))
  this.addEventListener('change', this.onspaceElementChanged.bind(this))

  this.addEventListener('onspace:input-association:change', this.onspaceElementChanged.bind(this))
  this.addEventListener('input-array:add-control', this.onspaceElementChanged.bind(this))
  this.addEventListener('input-array:order', this.onspaceElementChanged.bind(this))
  this.addEventListener('input-array:remove-control', this.onspaceElementChanged.bind(this))
  this.addEventListener('form-item:add-control', this.onspaceElementChanged.bind(this))
  this.addEventListener('form-item:order', this.onspaceElementChanged.bind(this))
  this.addEventListener('form-item:remove-control', this.onspaceElementChanged.bind(this))
  this.addEventListener('onspace:rich-text:change', this.onspaceElementChanged.bind(this))
}

/// Determines if the element has a child form which has changed.
Object.defineProperty(HTMLElement.prototype, 'onspaceShouldPromptFormChanges', {
  get: function() {
    const forms = Array.from(this.querySelectorAll('form'))
    return forms.some((f) => f.onspaceHasChanges && f.onspacePromptsChanges)
  }
})
Object.defineProperty(document, 'onspaceShouldPromptFormChanges', {
  get: function() {
    const forms = Array.from(this.querySelectorAll('form'))
    return forms.some((f) => f.onspaceHasChanges && f.onspacePromptsChanges)
  }
})

/// Queues an autosave to occur in the future.
///
/// This can be called multiple times in quick succession, and throttle the calls.
HTMLFormElement.prototype.onspaceQueueAutosave = function() {
  if (!this.onspaceAutosaveUrl || this.onspaceAutosaveTimer) { return }
  if (this.onspaceIsAutosaving) {
    this.onspaceChangedWhileAutosaving = true
    return
  }

  this.onspaceAutosaveTimer = window.setTimeout(() => this.onspaceAutosave(), AUTOSAVE_PERIOD)
}

/// Performs an autosave for the form if it has changed since the last autosave.
HTMLFormElement.prototype.onspaceAutosaveIfChanged = function() {
  if (!this.onspaceAutosaveUrl || !this.onspaceHasChanges) { return }
  if (!this.onspaceAutosaveTimer) { return }

  this.onspaceAutosave()
}

/// Performs the autosave function for the form.
///
/// This will collect the form's data and send it using the form's autosave url attribute. On completion it will update
/// any autosave indicators with the latest time.
HTMLFormElement.prototype.onspaceAutosave = async function(button) {
  if (!this.onspaceAutosaveUrl || !this.onspaceHasChanges) { return }

  if (this.onspaceAutosaveTimer) { window.clearTimeout(this.onspaceAutosaveTimer) }
  this.onspaceAutosaveTimer = null
  this.onspaceIsAutosaving = true
  this.onspaceChangedWhileAutosaving = false

  const formData = new FormData(this)
  const formObject = formData.onspaceToObject()
  delete formObject['_method']

  if (button) {
    this.onspaceShowLoading({ indicators: [button] })
  } else {
    this.onspaceShowLoading({ disable: false, indicators: [...this.onspaceAutosaveButtons, ...this.onspaceAutosaveIndicators] })
  }

  this.onspaceLocalSave()

  let response = null
  try {
    response = await client.put(this.onspaceAutosaveUrl, { params: formObject })
  } catch (error) {
    // ignore
  }

  if (response && response.isSuccess) {
    this.onspaceClearLocalSave()

    const responseContent = await response.content()
    if (typeof responseContent.autosaved_at === 'string') {
      const autosavedElement = `<formatted-time data-format="datetime" data-date="${responseContent.autosaved_at}" />`
      this.onspaceAutosaveIndicators.forEach((indicator) => indicator.innerHTML = autosavedElement)
    }
    if (typeof responseContent.draft === 'string') {
      const location = new URL(window.location.href)
      location.searchParams.set('draft', responseContent.draft)
      window.history.replaceState(window.history.state, null, location)

      const autosaveUrl = new URL(this.onspaceAutosaveUrl, window.location.href)
      autosaveUrl.searchParams.set('draft', responseContent.draft)
      this.onspaceAutosaveUrl = autosaveUrl.href

      this.querySelectorAll('[onspace-autosave-draft]').forEach((element) => {
        switch (element.tagName) {
        case 'INPUT':
          element.value = responseContent.draft
          break
        case 'A': {
          const href = new URL(element.href, window.location.href)
          href.searchParams.set('draft', responseContent.draft)
          element.href = href.href
          break
        }
        }
      })
    }
    if (responseContent.blobs && typeof responseContent.blobs === 'object') {
      this.querySelectorAll('input-blob-file input[type=hidden]').forEach((input) => {
        const signedId = responseContent.blobs[input.value]
        if (typeof signedId === 'string') {
          input.value = signedId
        }
      })
    }
  } else {
    const failedIcon = SVGElement.createOnspaceSpritemapSvg('onspace/icon_warning')
    const autosavedElement = `<div class="onspace-autosave-failed">${failedIcon.outerHTML}${translation('onspace.navigation.form_autosave.failed')}</div>`
    this.onspaceAutosaveIndicators.forEach((indicator) => indicator.innerHTML = autosavedElement)
  }

  this.onspaceIsAutosaving = false

  if (this.onspaceChangedWhileAutosaving) {
    this.onspaceChangedWhileAutosaving = false
    this.onspaceQueueAutosave()
  }

  this.onspaceHideLoading()
}

/// Stores the autosave data locally in the browser.
///
/// This will collect the form's data and store it using localStorage.
HTMLFormElement.prototype.onspaceLocalSave = function() {
  if (!this.onspaceAutosaveUrl || !this.onspaceHasChanges) { return }

  const formData = new FormData(this)
  const formObject = formData.onspaceToObject()
  delete formObject['_method']
  delete formObject['authenticity_token']

  const storageKey = `onspace:autosave:${this.onspaceAutosaveUrl}`
  const storageData = JSON.stringify(formObject)
  window.localStorage.setItem(storageKey, storageData)
}

/// Locates and attempts to restore locally saved data.
HTMLFormElement.prototype.onspaceRestoreLocalSave = async function() {
  if (!this.onspaceAutosaveUrl) { return }

  const storageKey = `onspace:autosave:${this.onspaceAutosaveUrl}`
  const storageData = window.localStorage.getItem(storageKey)

  if (!storageData) { return }

  const formObject = JSON.parse(storageData)
  const confirmed = window.confirm(translation('onspace.navigation.form_autosave.local_restore'))

  if (confirmed) {
    this.onspaceShowLoading()

    const response = await client.put(this.onspaceAutosaveUrl, { params: formObject })
    if (response.isSuccess) {
      const responseContent = await response.content()
      const location = new URL(window.location.href)
      if (typeof responseContent.draft === 'string') {
        location.searchParams.set('draft', responseContent.draft)
      }

      this.onspaceClearLocalSave()
      window.Turbo.visit(location)
    } else {
      this.onspaceHideLoading()
    }
  } else {
    this.onspaceClearLocalSave()
  }
}

/// Clears any autosave data stored locally in the browser.
HTMLFormElement.prototype.onspaceClearLocalSave = function() {
  if (!this.onspaceAutosaveUrl) { return }

  const storageKey = `onspace:autosave:${this.onspaceAutosaveUrl}`
  window.localStorage.removeItem(storageKey)
}

////////// Submission

const formSubmissionCallbacks = []

/// Registers a function to be run whenever a form is submitted.
///
/// All functions registered here will be called in parallel when a form is submitted, and they can perform their
/// processing either synchronously or asynchronously. If processing fails, they should throw an error.
export function registerFormSubmissionCallback(callback) {
  formSubmissionCallbacks.push(callback)
}

/// Captures form submissions.
///
/// This inserts itself between a user submitting a form, and the form actually being submitted to the server. It will
/// handle loading UI, and run registered processing functions in parallel.
///
/// Note that in performing this function, it will cancel the initial event, and resubmit when completed.
HTMLFormElement.prototype.onspaceHandleSubmission = async function(event) {
  if (this.onspaceSubmissionHandled) { return }

  event.preventDefault()

  const buttons = this.querySelectorAll('input[type=submit], button[type=submit]')
  this.onspaceShowLoading({ indicators: buttons })

  try {
    await Promise.all(formSubmissionCallbacks.map((callback) => callback(this)))

    this.onspaceSubmissionHandled = true
  } catch (error) {
    console.error(error) // eslint-disable-line no-console
  } finally {
    this.onspaceHideLoading()
  }

  if (this.onspaceSubmissionHandled) {
    setTimeout(() => {
      this.requestSubmit(event.submitter)
    })
  }
}

/// Displays loading UI for the form.
///
/// This accepts the following parameters:
/// [:disable]
///   Indicates whether the form and its controls should be disabled. This is +true+ by default.
/// [:indicators]
///   Elements which should indicate the page is loading, which will be given a loading indicator. If not provided, the
///   form's submit buttons will be used. Set this to +false+ to disable this feature.
HTMLFormElement.prototype.onspaceShowLoading = function({ disable = true, indicators } = {}) {
  if (disable) {
    this.querySelectorAll('fieldset').forEach((element) => element.disabled = true)
  }

  if (typeof indicators === 'undefined') {
    indicators = this.querySelectorAll('input[type=submit], button[type=submit]')
  }

  this.onspaceLoadingIndicators = indicators
  if (indicators) {
    indicators.forEach((indicator) => {
      const icon = SVGElement.createOnspaceSpritemapSvg('onspace/icon_loading')
      indicator.appendChild(icon)
      indicator.onspaceFormLoadingIcon = icon
    })
  }
}

document.addEventListener('turbo:submit-start', (event) => {
  const submission = event.detail.formSubmission
  submission.fetchRequest.headers['Turbo-Action'] = 'submit'

  const form = submission.formElement
  form.onspaceLocalSave()
  form.onspaceShowLoading()

  form.onspaceHasChanges = false
})

////////// Completion

const formCompletionCallbacks = []

/// Registers a function to be run whenever a form submission is completed.
///
/// All functions registered here will be called in synchronously when a form completes its submission.
export function registerFormCompletionCallback(callback) {
  formCompletionCallbacks.push(callback)
}

/// Removes loading UI from the form.
HTMLFormElement.prototype.onspaceHideLoading = function enableForm() {
  this.querySelectorAll('fieldset').forEach((element) => element.disabled = false)

  if (this.onspaceLoadingIndicators) {
    this.onspaceLoadingIndicators.forEach((indicator) => {
      indicator.onspaceFormLoadingIcon.remove()
      indicator.onspaceFormLoadingIcon = null
    })
  }

  this.onspaceLoadingIndicators = null
}

document.addEventListener('turbo:submit-end', (event) => {
  const submission = event.detail.formSubmission
  const response = event.detail.fetchResponse

  const form = submission.formElement

  formCompletionCallbacks.forEach((callback) => callback(form))

  if (event.detail.success || (response && response.clientError)) {
    form.onspaceClearLocalSave()

    if (response.contentType && response.contentType.match(/^text\/vnd\.turbo-stream.html/)) {
      form.onspaceHideLoading()
    }
  } else if (event.detail.error) {
    form.onspaceHideLoading()
    window.alert(translation('onspace.navigation.form.submit_failed'))
  }
})

////////// Form Data

/// Extracts the underlying data into a Javascript Object.
///
/// This will identify and parse array fields.
FormData.prototype.onspaceToObject = function() {
  const object = {}

  for (const key of this.keys()) {
    if (key.endsWith('[]')) {
      let values = this.getAll(key)
      if (values.length > 1) {
        values = values.filter((value) => value !== '')
      }

      const objectKey = key.replace(/\[\]$/, '')
      object[objectKey] = values
    } else {
      const values = this.getAll(key)
      object[key] = values[values.length - 1]
    }
  }

  return object
}
